/**
 * «ALQUILER QUIRÓFANO EXTERNOS» (administración, 23/10/2026): el cuarto
 * desplegable del Catálogo de cirugías, tratamientos y otros costes, al mismo
 * nivel que Tratamientos, Cirugías y Hospitales.
 *
 * Tiene TODAS las tarifas que usa el cálculo del Registro de externos y es la
 * ÚNICA fuente: se editan aquí y el Registro las lee de aquí (GET/PUT
 * /api/tarifas-externos, la misma colección versionada de siempre). Aquí no
 * hay ni una cifra: todo viene del servidor, y un campo vacío es una tarifa
 * que falta (la intervención que la necesita sale «Pendiente de consulta»).
 *
 *  - Lo CONSULTA quien entra en el Catálogo.
 *  - Lo EDITA solo quien tiene en Usuarios la casilla «Edita las tarifas de
 *    externos» (`puede_editar` lo dice el servidor, que da 403 al resto,
 *    también a un administrador sin la casilla).
 *  - Cada guardado es una VERSIÓN nueva con su «vigente desde»; las
 *    anteriores se conservan (cada intervención se cobra con la de su fecha).
 *  - La cirugía con CO2 tiene su propio precio. En una versión de antes, que
 *    no lo tenía, sale CALCULADO (base + suplemento láser de esa versión) y,
 *    al guardar, se queda como precio propio.
 *
 * Se abre solo con ?bloque=alquiler-externos (el enlace del Registro de
 * externos).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, formatEUR, mensajeError } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';

export const BLOQUE_EXTERNOS = 'alquiler-externos';

const VACIO = {
  definicion: { grupos: [], claves: [] },
  tarifas: { valores: {}, consultar: [], instrumentista_propia: { clinicas: [], cirujanos: [] } },
  versiones: [],
  faltan: [],
  co2: { precio: null, derivado: false },
  opciones_instrumentista: { clinicas: [], cirujanos: [] },
  nombres_cirujanos: {},
  puede_editar: false,
  hoy: '',
};

const esObjeto = (v) => v && typeof v === 'object' && !Array.isArray(v);
const comoTexto = (v) => (v == null ? '' : String(v).replace('.', ','));
const fechaEs = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');

/** «2 h 30 min», «2 h», «45 min». */
export function minutosEnHoras(m) {
  if (m == null || m === '' || Number.isNaN(Number(m))) return '';
  const n = Number(m);
  const h = Math.floor(n / 60);
  const r = n % 60;
  if (!h) return `${r} min`;
  return r ? `${h} h ${r} min` : `${h} h`;
}

/** Una tarifa como se lee: «1.000,00 €», «10 min», «21 %», «—». El número,
 *  tal como lo manda el servidor (no el texto del campo, que lleva coma
 *  decimal: «512,5» daba «NaN €», revisión del 24/10/2026). */
export function textoTarifa(valor, unidad) {
  if (valor == null || valor === '' || Number.isNaN(Number(valor))) return '—';
  if (unidad === 'min') return `${valor} min`;
  if (unidad === 'pct') return `${String(valor).replace('.', ',')} %`;
  return formatEUR(valor);
}

const UNIDAD_CORTA = { eur: '€', min: 'min', pct: '%' };

// Lo que se explica, sin cifras, debajo de cada grupo.
const AYUDA = {
  cirugia: 'Hora extra entera en cuanto el exceso llega al umbral (el umbral justo ya cuenta). El instrumentista, salvo que traigan el suyo; el circulante va siempre incluido.',
  laser: 'Sin cirugía. Marca «Consultar antes de cobrar» en la tarifa pendiente de renegociar: el Registro de externos avisa antes de cobrarla.',
  retoque: 'Según dónde se hizo la primera cirugía y si lleva sedación o anestesia. Si pasa de la duración máxima, se cobra como cirugía.',
  tiempo: 'Empieza a contar al sedar o, como muy tarde, a la hora de la cita más la cortesía. Si antes operaba otro equipo en el mismo sitio y seguía en la sala, desde la entrada real, sin cortesía (el exceso se puede perdonar en el Registro de externos). Si era el mismo cirujano, la segunda empieza a contar la separación después de que acabe la anterior. Acaba cuando el paciente sale por la puerta.',
  iva: 'Solo para el PDF «Con IVA» del Registro de externos: las tarifas son siempre sin IVA.',
};

export default function AlquilerQuirofanoExternos({ Desplegable, abierto, onToggle }) {
  const [datos, setDatos] = useState(VACIO);
  const [estado, setEstado] = useState('cargando');
  const [valores, setValores] = useState({});
  const [consultar, setConsultar] = useState([]);
  const [clinicas, setClinicas] = useState([]);
  const [cirujanos, setCirujanos] = useState([]);
  const [vigenteDesde, setVigenteDesde] = useState('');
  const [guardando, setGuardando] = useState(false);

  const aplicar = useCallback((d) => {
    const t = d.tarifas || VACIO.tarifas;
    const vals = Object.fromEntries(Object.entries(t.valores || {}).map(([k, v]) => [k, comoTexto(v)]));
    // El precio con CO2 de una versión de antes, calculado: se enseña y, al
    // guardar, se queda como precio propio.
    if (d.co2?.derivado && d.co2?.precio != null) vals.cirugia_base_co2 = comoTexto(d.co2.precio);
    setDatos(d);
    setValores(vals);
    setConsultar(t.consultar || []);
    setClinicas(t.instrumentista_propia?.clinicas || []);
    setCirujanos(t.instrumentista_propia?.cirujanos || []);
    setVigenteDesde(d.hoy || '');
  }, []);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/api/tarifas-externos');
      const d = esObjeto(r?.data) ? r.data : {};
      aplicar({
        ...VACIO,
        ...d,
        definicion: { grupos: d.definicion?.grupos || [], claves: d.definicion?.claves || [] },
        co2: esObjeto(d.co2) ? d.co2 : VACIO.co2,
        opciones_instrumentista: {
          clinicas: d.opciones_instrumentista?.clinicas || [],
          cirujanos: d.opciones_instrumentista?.cirujanos || [],
        },
        versiones: Array.isArray(d.versiones) ? d.versiones : [],
        faltan: Array.isArray(d.faltan) ? d.faltan : [],
        puede_editar: d.puede_editar === true,
      });
      setEstado('listo');
    } catch (e) {
      setEstado(mensajeError(e, 'no se han podido leer'));
    }
  }, [aplicar]);
  useEffect(() => { cargar(); }, [cargar]);

  const editable = datos.puede_editar === true && estado === 'listo';
  const claves = datos.definicion.claves.filter((c) => !c.antigua);
  const etiqueta = Object.fromEntries(datos.definicion.claves.map((c) => [c.clave, c]));
  const vigente = datos.tarifas?.id ? datos.tarifas : null;
  const anteriores = datos.versiones.filter((v) => !vigente || v.id !== vigente.id);
  const minutos = valores.cirugia_minutos_incluidos;
  // El número del servidor de cada tarifa (para leerla); el del CO2 de una
  // versión de antes, el calculado.
  const delServidor = (clave) => {
    if (clave === 'cirugia_base_co2' && datos.co2?.derivado) return datos.co2.precio;
    const v = datos.tarifas?.valores?.[clave];
    return v == null ? null : v;
  };
  const hoy = datos.hoy || '';
  const fechaPasada = !!(vigenteDesde && hoy && vigenteDesde < hoy);
  const alterna = (lista, setLista, v) => setLista(lista.includes(v) ? lista.filter((x) => x !== v) : [...lista, v]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const cuerpo = {
        vigente_desde: vigenteDesde,
        valores: Object.fromEntries(claves.map((c) => {
          const v = String(valores[c.clave] ?? '').trim();
          return [c.clave, v === '' ? null : v];
        })),
        consultar,
        instrumentista_propia: { clinicas, cirujanos },
      };
      const r = await api.put('/api/tarifas-externos', cuerpo);
      toast.success(`Tarifas de externos guardadas, vigentes desde el ${fechaEs(vigenteDesde)}`);
      if (esObjeto(r?.data) && r.data.definicion) {
        aplicar({ ...VACIO, ...r.data, puede_editar: r.data.puede_editar === true });
      } else {
        cargar();
      }
    } catch (e) {
      toast.error(mensajeError(e, 'No se han podido guardar las tarifas'));
    } finally {
      setGuardando(false);
    }
  };

  const resumen = estado !== 'listo' ? ''
    : `${vigente ? `Vigente desde el ${fechaEs(vigente.vigente_desde)}` : 'Sin tarifas'}${
      datos.faltan.length ? ` · faltan ${datos.faltan.length}` : ''}`;

  const pista = (clave) => {
    if ((clave === 'cirugia_base' || clave === 'cirugia_base_co2') && minutosEnHoras(minutos)) {
      return `hasta ${minutosEnHoras(minutos)}`;
    }
    return '';
  };

  const fila = (c) => (
    <tr key={c.clave} className="border-b border-border/60 align-top" data-testid={`externos-tarifa-fila-${c.clave}`}>
      <td className="py-2 pr-3">
        <label htmlFor={`tarifa-ext-${c.clave}`} className="font-medium text-[#13231E]">{c.etiqueta}</label>
        {pista(c.clave) && <span className="block text-xs text-muted-foreground">{pista(c.clave)}</span>}
        {c.clave === 'cirugia_base_co2' && datos.co2?.derivado && (
          <span className="block text-xs text-amber-800" data-testid="externos-tarifa-co2-calculado">
            Calculado con la versión vigente (precio sin CO2 + suplemento láser de antes).
            {editable ? ' Al guardar, queda como precio propio.' : ''}
          </span>
        )}
      </td>
      <td className="py-2 px-3 text-right whitespace-nowrap w-48">
        {editable ? (
          <span className="inline-flex items-center gap-1">
            <Input
              id={`tarifa-ext-${c.clave}`}
              type="text"
              inputMode="decimal"
              className="w-28 h-8 text-right tabular-nums"
              value={valores[c.clave] ?? ''}
              placeholder="sin tarifa"
              onChange={(e) => setValores((v) => ({ ...v, [c.clave]: e.target.value }))}
              data-testid={`externos-tarifa-${c.clave}`}
            />
            <span className="text-xs text-muted-foreground w-7 text-left">{UNIDAD_CORTA[c.unidad] || ''}</span>
          </span>
        ) : (
          <span className={`tabular-nums font-semibold ${delServidor(c.clave) != null ? '' : 'text-amber-800'}`}
            data-testid={`externos-tarifa-valor-${c.clave}`}>
            {delServidor(c.clave) != null ? textoTarifa(delServidor(c.clave), c.unidad) : 'Falta'}
          </span>
        )}
      </td>
      <td className="py-2 pl-3 w-56 text-xs">
        {c.unidad === 'eur' && (editable ? (
          <label className="inline-flex items-center gap-1.5 text-muted-foreground cursor-pointer">
            <Checkbox checked={consultar.includes(c.clave)} onCheckedChange={() => alterna(consultar, setConsultar, c.clave)}
              data-testid={`externos-tarifa-consultar-${c.clave}`} />
            Consultar antes de cobrar
          </label>
        ) : consultar.includes(c.clave) ? (
          <span className="rounded bg-amber-50 border border-amber-200 text-amber-900 px-1.5 py-0.5"
            data-testid={`externos-tarifa-consultar-${c.clave}`}>
            Consultar antes de cobrar
          </span>
        ) : null)}
      </td>
    </tr>
  );

  const nombresClinicas = new Set(datos.opciones_instrumentista.clinicas);
  const clinicasFuera = clinicas.filter((n) => !nombresClinicas.has(n));
  const idsExternos = new Set(datos.opciones_instrumentista.cirujanos.map((c) => c.id));
  const cirujanosFuera = cirujanos.filter((id) => !idsExternos.has(id));

  return (
    <Desplegable principal testid="catalogo-externos" titulo="ALQUILER QUIRÓFANO EXTERNOS"
      resumen={resumen} abierto={abierto} onToggle={onToggle}>
      {estado === 'cargando' && <p className="text-sm text-muted-foreground">Cargando las tarifas…</p>}
      {estado !== 'cargando' && estado !== 'listo' && (
        <p className="text-sm text-red-700" role="alert" data-testid="catalogo-externos-fallo">
          No se han podido leer las tarifas de externos: {estado}.
        </p>
      )}
      {estado === 'listo' && (
        <div className="space-y-4" data-testid="catalogo-externos-contenido">
          <p className="text-xs text-muted-foreground max-w-3xl">
            Importes SIN IVA. El Registro de externos calcula con estas tarifas, con la versión vigente en la
            fecha de cada intervención. Un campo vacío es una tarifa que falta: la intervención que la necesita
            sale «Pendiente de consulta».
            {!editable && ' Solo quien tiene «Edita las tarifas de externos» en Usuarios puede cambiarlas.'}
          </p>
          <div className="text-xs text-muted-foreground" data-testid="catalogo-externos-vigente">
            {vigente
              ? `Versión vigente hoy: desde el ${fechaEs(vigente.vigente_desde)}${vigente.creado_por_name ? `, guardada por ${vigente.creado_por_name}` : ''}.`
              : 'Todavía no hay ninguna versión de tarifas vigente hoy.'}
          </div>
          {datos.faltan.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="catalogo-externos-faltan">
              Faltan {datos.faltan.length}: {datos.faltan.map((f) => f.etiqueta).join(' · ')}.
            </div>
          )}
          {editable && (
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="externos-vigente-desde" className="text-xs font-medium">Vigente desde</label>
              <Input id="externos-vigente-desde" type="date" className="w-44 h-8" value={vigenteDesde}
                onChange={(e) => setVigenteDesde(e.target.value)} data-testid="catalogo-externos-vigente-desde" />
              <span className="text-xs text-muted-foreground">Guardar crea una versión nueva desde esa fecha; las anteriores se conservan.</span>
              <p className={`basis-full text-xs ${fechaPasada ? 'text-amber-800 font-medium' : 'text-muted-foreground'}`}
                data-testid="catalogo-externos-vigente-explica">
                El cambio vale para las cirugías desde esa fecha, incluidas las ya hechas si la fecha es anterior a
                hoy; las de antes de esa fecha siguen con la versión que tenían.
                {fechaPasada ? ` Con el ${fechaEs(vigenteDesde)}, cambia lo que se factura de las cirugías ya hechas desde ese día.` : ''}
              </p>
            </div>
          )}
          {datos.definicion.grupos.map((g) => {
            const delGrupo = claves.filter((c) => c.grupo === g.clave);
            if (!delGrupo.length) return null;
            const titulo = g.clave === 'retoque' && valores.retoque_duracion_max_min
              ? `${g.titulo} (máximo ${minutosEnHoras(valores.retoque_duracion_max_min)})`
              : g.clave === 'cirugia' && minutosEnHoras(minutos) ? `${g.titulo} (hasta ${minutosEnHoras(minutos)})` : g.titulo;
            return (
              <div key={g.clave} className="rounded-md border border-border p-3" data-testid={`catalogo-externos-grupo-${g.clave}`}>
                <h3 className="text-base font-semibold text-[#13231E]">{titulo}</h3>
                {AYUDA[g.clave] && <p className="text-xs text-muted-foreground mt-0.5 mb-1">{AYUDA[g.clave]}</p>}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[620px]">
                    <tbody>{delGrupo.map(fila)}</tbody>
                  </table>
                </div>
              </div>
            );
          })}
          <div className="rounded-md border border-border p-3" data-testid="catalogo-externos-instrumentista">
            <h3 className="text-base font-semibold text-[#13231E]">Traen instrumentista propia</h3>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">A estas clínicas y cirujanos no se les cobra el suplemento de instrumentista.</p>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs font-medium mb-1">Clínicas de procedencia</div>
                {editable ? (
                  <>
                    {datos.opciones_instrumentista.clinicas.map((n) => (
                      <label key={n} className="flex items-center gap-2 py-0.5 cursor-pointer">
                        <Checkbox checked={clinicas.includes(n)} onCheckedChange={() => alterna(clinicas, setClinicas, n)}
                          data-testid="catalogo-externos-clinica" />
                        <span className="truncate">{n}</span>
                      </label>
                    ))}
                    {clinicasFuera.map((n) => (
                      <label key={`fuera-${n}`} className="flex items-center gap-2 py-0.5 cursor-pointer">
                        <Checkbox checked onCheckedChange={() => alterna(clinicas, setClinicas, n)} />
                        <span className="truncate">{n} <span className="text-xs text-amber-700">(ya no está en el catálogo)</span></span>
                      </label>
                    ))}
                  </>
                ) : (
                  <p data-testid="catalogo-externos-clinicas-propia">{clinicas.length ? clinicas.join(' · ') : 'Ninguna'}</p>
                )}
              </div>
              <div>
                <div className="text-xs font-medium mb-1">Cirujanos externos</div>
                {editable ? (
                  <>
                    {datos.opciones_instrumentista.cirujanos.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 py-0.5 cursor-pointer">
                        <Checkbox checked={cirujanos.includes(c.id)} onCheckedChange={() => alterna(cirujanos, setCirujanos, c.id)}
                          data-testid="catalogo-externos-cirujano" />
                        <span className="truncate">{c.nombre}</span>
                      </label>
                    ))}
                    {cirujanosFuera.map((id) => (
                      <label key={`fuera-${id}`} className="flex items-center gap-2 py-0.5 cursor-pointer">
                        <Checkbox checked onCheckedChange={() => alterna(cirujanos, setCirujanos, id)} />
                        <span className="truncate">{datos.nombres_cirujanos[id] || id} <span className="text-xs text-amber-700">(ya no está como externo)</span></span>
                      </label>
                    ))}
                  </>
                ) : (
                  <p data-testid="catalogo-externos-cirujanos-propia">
                    {cirujanos.length ? cirujanos.map((id) => datos.nombres_cirujanos[id] || id).join(' · ') : 'Ninguno'}
                  </p>
                )}
              </div>
            </div>
          </div>
          {anteriores.length > 0 && (
            <div className="rounded-md border border-dashed border-border p-3" data-testid="catalogo-externos-versiones">
              <h4 className="text-sm font-semibold text-[#13231E]">Otras versiones</h4>
              {anteriores.map((v) => (
                <details key={v.id} className="text-xs py-1">
                  <summary className="cursor-pointer">
                    Desde el {fechaEs(v.vigente_desde)}{v.creado_por_name ? ` · ${v.creado_por_name}` : ''}
                  </summary>
                  <ul className="mt-1 ml-4 list-disc text-muted-foreground">
                    {Object.entries(v.valores || {}).filter(([, x]) => x != null).map(([k, x]) => (
                      <li key={k}>{etiqueta[k]?.etiqueta || k}: {textoTarifa(x, etiqueta[k]?.unidad)}</li>
                    ))}
                  </ul>
                </details>
              ))}
            </div>
          )}
          {editable && (
            <div className="flex justify-end">
              <Button onClick={guardar} disabled={guardando || claves.length === 0}
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="catalogo-externos-guardar">
                {guardando ? 'Guardando…' : 'Guardar tarifas'}
              </Button>
            </div>
          )}
        </div>
      )}
    </Desplegable>
  );
}
